/**
 * useGroundStatusSweep
 *
 * Runs in the TrackScreen (main.jsx) on mount and whenever the flights list
 * refreshes. Does a one-time background sweep for pre-flight status changes:
 *
 *  1. IN_TRANSIT (FR24)    — aircraft already airborne when user opens the app
 *  2. READY (FR24 + proximity) — aircraft on ground within 10 km of departure
 *                                airport (when live position alt === 0)
 *  3. DELAYED (time-based, FR24-gated) — departure + 2 h passed AND FR24 has no
 *     evidence the flight departed. If FR24 shows it flew, the flight becomes
 *     IN_TRANSIT / COMPLETED / DIVERTED instead of being mislabelled DELAYED.
 *
 * Runs at most once per session per flight to avoid hammering the API.
 * All updates are fire-and-forget; cache is invalidated after batch completes.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { deriveQuoteStatus, resolveDepartedFromSummary } from '@/features/track/utils/flightStatusUtils';
import { isPastReviewThreshold, markNeedsReview, shouldSkipFr24 } from '@/features/track/utils/needsReview';

const SWEPT_KEY = 'gtj_ground_sweep_ids'; // sessionStorage key
const PRE_FLIGHT = ['PENDING', 'POSITIONING', 'READY', 'DELAYED'];
const AIRBORNE_ALT_FT = 1000;
const READY_RADIUS_KM = 10;
// Don't consider "airborne" evidence for IN_TRANSIT unless the flight's
// scheduled departure is within this window. Protects against the case
// where the booked aircraft is flying some unrelated trip hours or days
// before our departure (TR-2610-01328 class — flight was flipped
// IN_TRANSIT on Oct 19 for a Oct 21 departure because the tail was
// airborne on another trip at the time of the sweep).
const AIRBORNE_PROXIMITY_HOURS = 6;

// API returns flight_id / quote_id; tolerate either shape.
const getFlightId = (f) => f?.flight_id || f?.id;
const getQuoteId = (f) => f?.quote_id || f?.quote?.quote_id || f?.quote?.id;

// Haversine distance in km (duplicated here to avoid import dependency)
function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function useGroundStatusSweep(rawFlights) {
  const queryClient = useQueryClient();
  const isRunningRef = useRef(false); // prevents concurrent runs only, not future ones

  useEffect(() => {
    if (isRunningRef.current || !rawFlights?.length) return;
    isRunningRef.current = true;
    runSweep(rawFlights, queryClient).finally(() => {
      isRunningRef.current = false;
    });
  }, [rawFlights, queryClient]); // re-runs on pull-to-refresh; sessionStorage deduplicates per-flight
}

async function runSweep(rawFlights, queryClient) {
  // Only sweep pre-flight flights that haven't been swept this session
  const sweptIds = new Set(
    JSON.parse(sessionStorage.getItem(SWEPT_KEY) || '[]')
  );

  const candidates = rawFlights.filter((f) => {
    const status = (f.flight_status || '').toUpperCase();
    return PRE_FLIGHT.includes(status) && !sweptIds.has(getFlightId(f)) && f.departure_date;
  });

  if (!candidates.length) return;

  const updates = []; // { flightId, newStatus }
  const now = Date.now();

  // Past-due = departure + 2 h elapsed and not already DELAYED. These are the
  // only flights eligible for a DELAYED label — but only as a fallback once
  // FR24 confirms the aircraft genuinely hasn't departed (step 2).
  const pastDue = candidates.filter((f) => {
    const status = (f.flight_status || '').toUpperCase();
    if (status === 'DELAYED') return false;
    const delayedAt = new Date(f.departure_date).getTime() + 2 * 60 * 60 * 1000;
    return now > delayedAt;
  });

  // ── 1. FR24 live positions: airborne → IN_TRANSIT, on-ground near dep → READY ──
  const airborneRegs = new Set(); // registrations FR24 currently reports flying
  const flightsWithTail = candidates.filter((f) => f.aircraft?.tail_number);

  if (flightsWithTail.length) {
    try {
      const registrations = flightsWithTail
        .map((f) => f.aircraft.tail_number)
        .join(',');

      const response = await apiClient.getLiveFlightPositions(registrations);
      const livePositions = response?.data || [];

      for (const pos of livePositions) {
        const alt = pos.alt ?? 0;
        const reg = (pos.reg || '').toUpperCase();

        const flight = flightsWithTail.find(
          (f) => f.aircraft.tail_number.toUpperCase() === reg
        );
        if (!flight) continue;

        const currentStatus = (flight.flight_status || '').toUpperCase();

        // Airborne → IN_TRANSIT (guarded by route match + departure proximity)
        if (alt > AIRBORNE_ALT_FT) {
          airborneRegs.add(reg);

          // Guard 1: departure proximity. Flights more than N hours
          // out can't legitimately be "in transit" yet — the tail is
          // just doing an unrelated trip. Mirrors common sense; the
          // detail-page hook assumes it's always on the day-of.
          const depMs = flight.departure_date
            ? new Date(flight.departure_date).getTime()
            : null;
          const proximityOk = depMs == null
            || Math.abs(depMs - now) <= AIRBORNE_PROXIMITY_HOURS * 60 * 60 * 1000;

          // Guard 2: route match. FR24 reports the live flight's
          // origin and destination; only accept IN_TRANSIT when one
          // of them matches this trip. Matches useFlightStatusAutomation.js
          // :148-154. If FR24 can't tell us the route, fall through
          // and trust the altitude (same as the detail hook).
          const liveOrig = (pos.orig_icao || pos.orig_iata || '').toUpperCase();
          const liveDest = (pos.dest_icao || pos.dest_iata || '').toUpperCase();
          const depAp = (flight.departure_airport || '').toUpperCase();
          const arrAp = (flight.arrival_airport || '').toUpperCase();
          const routeOk =
            !liveOrig || !liveDest || // FR24 silent on route — trust altitude
            liveOrig === depAp ||
            liveDest === arrAp;

          if (currentStatus !== 'IN_TRANSIT' && proximityOk && routeOk) {
            updates.push({ flight, newStatus: 'IN_TRANSIT' });
          }
          continue;
        }

        // On ground — check READY (within 10 km of departure airport)
        if (alt === 0 && currentStatus === 'POSITIONING' && pos.lat != null && pos.lon != null) {
          const depCode = flight.departure_airport;
          if (depCode) {
            try {
              const airport = await apiClient.getAirportFull(depCode);
              if (airport?.lat != null && airport?.lon != null) {
                const dist = haversine(pos.lat, pos.lon, airport.lat, airport.lon);
                if (dist <= READY_RADIUS_KM) {
                  updates.push({ flight, newStatus: 'READY' });
                }
              }
            } catch {
              // airport lookup failed — skip READY for this flight
            }
          }
        }
      }
    } catch (err) {
      console.error('[GroundSweep] FR24 batch check failed:', err);
    }
  }

  // ── 2. Resolve past-due flights with FR24 evidence before DELAYED ─────────
  // For each past-due flight not already seen airborne, ask FR24 whether it
  // actually flew. An ended summary on the booked route → COMPLETED/DIVERTED;
  // a mid-flight summary → IN_TRANSIT; only when FR24 is silent (no tail / no
  // matching record) do we fall back to the time-based DELAYED label.
  await Promise.allSettled(
    pastDue.map(async (flight) => {
      const reg = (flight.aircraft?.tail_number || '').toUpperCase();
      if (reg && airborneRegs.has(reg)) return; // already queued IN_TRANSIT above

      // Already determined unresolvable — skip the costly FR24 query entirely.
      if (shouldSkipFr24(flight)) return;

      const fromSummary = await resolveDepartedFromSummary(flight);
      if (fromSummary) {
        updates.push({ flight, newStatus: fromSummary });
        return;
      }
      // FR24 silent. Past the 24h review threshold → flag for human review and
      // stop querying. Between 2–24h it's a normal DELAYED (worth re-checking).
      if (isPastReviewThreshold(flight)) {
        markNeedsReview(flight);
      } else {
        updates.push({ flight, newStatus: 'DELAYED' });
      }
    })
  );

  if (!updates.length) {
    // Mark all candidates as swept even if nothing changed
    candidates.forEach((f) => sweptIds.add(getFlightId(f)));
    sessionStorage.setItem(SWEPT_KEY, JSON.stringify([...sweptIds]));
    return;
  }

  // Deduplicate: if a flight appears in multiple updates, take the highest-priority one
  const priority = { COMPLETED: 5, DIVERTED: 5, IN_TRANSIT: 3, READY: 2, DELAYED: 1 };
  const deduped = new Map();
  for (const u of updates) {
    const id = getFlightId(u.flight);
    const existing = deduped.get(id);
    if (!existing || (priority[u.newStatus] ?? 0) > (priority[existing.newStatus] ?? 0)) {
      deduped.set(id, u);
    }
  }

  await Promise.allSettled(
    [...deduped.values()].map(async ({ flight, newStatus }) => {
      const flightId = getFlightId(flight);
      try {

        await apiClient.patchFlight(flightId, { flight_status: newStatus });
        sweptIds.add(flightId);

        // Cascade to quote status
        const quoteId = getQuoteId(flight);
        if (quoteId) {
          const newQuoteStatus = await deriveQuoteStatus(quoteId, flightId, newStatus);
          if (newQuoteStatus) {

            await apiClient.updateQuote(quoteId, { status: newQuoteStatus });
          }
        }
      } catch (err) {
        console.error(`[GroundSweep] Update failed for ${flightId}:`, err);
      }
    })
  );

  sessionStorage.setItem(SWEPT_KEY, JSON.stringify([...sweptIds]));
  queryClient.invalidateQueries({ queryKey: ['flights'] });
}
